import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { confirmPasswordRecovery } from '../services/authService';
import TopBar from './TopBar';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const userId = searchParams.get('userId');
  const secret = searchParams.get('secret');

  useEffect(() => {
    if (!userId || !secret) {
      setError('Invalid or expired reset link. Please request a new one.');
    }
  }, [userId, secret]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordRecovery(userId, secret, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.code === 401) {
        setError('Reset link has expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <TopBar />

      {/* Main Content */}
      <main className="main-content">
        <div className="reset-password-container">
          {!success ? (
            <>
              <div className="title-section">
                <h1 className="title">Create New Password</h1>
                <p className="subtitle">
                  Enter your new password below.
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}

              {userId && secret && (
                <form onSubmit={handleSubmit} className="reset-password-form">
                  <div className="form-group">
                    <label htmlFor="password" className="form-label">New Password</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="form-input"
                      placeholder="Enter new password (min. 8 characters)"
                      minLength={8}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="form-input"
                      placeholder="Confirm new password"
                      minLength={8}
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              )}

              <div className="form-footer">
                <Link to="/login" className="back-link">
                  ← Back to login
                </Link>
              </div>
            </>
          ) : (
            <div className="success-container">
              <div className="success-icon">✅</div>
              <h1 className="title">Password Reset!</h1>
              <p className="subtitle">
                Your password has been successfully reset. Redirecting to login...
              </p>
              <Link to="/login" className="back-link">
                Go to login now
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
